import { useState, useContext, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import BuildersLogo from "@components/BuildersLogo";
import FormikInput from "../../components/form/FormikInput";
import HeadlessButton from "@components/Button";
import Spinner from "@components/Spinner";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../../components/constant_components/LanguageToggle";
import { SnackbarContext } from "../../components/constant_components/providers/SnackbarProvider";
import axiosClient from "@service/axiosClient";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { encrypted_code, type } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const { triggerSnackbar } = useContext(SnackbarContext);

  const validateToken = useCallback(async () => {
    try {
      const endpoint =
        type === "admin"
          ? "/admins/validate_reset_token"
          : "/users/validate_reset_token";
      const response = await axiosClient.post(endpoint, {
        encrypted_code,
      });
      if (response.data.valid) {
        setIsValidToken(true);
      } else {
        triggerSnackbar(
          t("resetPassword.invalidToken") || "Invalid or expired reset link"
        );
        setTimeout(() => navigate(`/login/${type || "user"}`), 3000);
      }
    } catch (error) {
      triggerSnackbar(
        error.response?.data?.msg ||
          t("resetPassword.invalidToken") ||
          "Invalid or expired reset link"
      );
      setTimeout(() => navigate(`/login/${type || "user"}`), 3000);
    } finally {
      setIsPageLoading(false);
    }
  }, [encrypted_code, type, t, triggerSnackbar, navigate]);

  useEffect(() => {
    document.title = t("resetPassword.title") || "Reset Password";
    validateToken();
  }, [t, validateToken]);

  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(
        8,
        t("resetPassword.password.minLength") ||
          "Password must be at least 8 characters"
      )
      .required(t("resetPassword.password.required") || "Password is required"),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("resetPassword.password.match") || "Passwords must match"
      )
      .required(
        t("resetPassword.confirmPassword.required") ||
          "Please confirm your password"
      ),
  });

  const handleSubmit = async (values) => {
    setIsLoading(true);

    try {
      const endpoint =
        type === "admin" ? "/admins/reset_password" : "/users/reset_password";
      await axiosClient.post(endpoint, {
        encrypted_code,
        new_password: values.password,
      });

      triggerSnackbar(
        t("resetPassword.success") || "Password reset successfully!"
      );
      setTimeout(() => navigate(`/login/${type || "user"}`), 2000);
    } catch (error) {
      triggerSnackbar(
        error.response?.data?.msg ||
          t("resetPassword.error") ||
          "Failed to reset password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-100 tw-relative">
      <div className="tw-absolute tw-top-4 tw-right-4">
        <LanguageToggle />
      </div>

      {isPageLoading ? (
        <div className="tw-flex tw-flex-col tw-items-center">
          <BuildersLogo />
          <div className="tw-mt-6 tw-w-[80%] md:tw-w-[30%]">
            <div className="tw-h-12 tw-bg-neutral-200 tw-animate-pulse tw-rounded-md"></div>
          </div>
        </div>
      ) : !isValidToken ? (
        <div className="tw-w-[90%] tw-max-w-lg tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-300">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <BuildersLogo />
          </div>
          <div className="tw-text-center tw-text-red-600">
            <p>
              {t("resetPassword.invalidToken") ||
                "Invalid or expired reset link"}
            </p>
            <p className="tw-text-sm tw-mt-2">
              {t("resetPassword.redirecting") || "Redirecting to login..."}
            </p>
          </div>
        </div>
      ) : (
        <div className="tw-w-[90%] tw-max-w-lg tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-300">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <BuildersLogo />
          </div>

          <h2 className="tw-text-2xl tw-text-center tw-text-primary-light tw-font-bold tw-mb-6">
            {t("resetPassword.title") || "Reset Your Password"}
          </h2>

          <p className="tw-text-center tw-text-gray-600 tw-mb-6">
            {t("resetPassword.instructions") ||
              "Please enter your new password below."}
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <div className="tw-mb-4">
                  <Field name="password">
                    {({ field, form }) => (
                      <FormikInput
                        {...field}
                        name="password"
                        label={
                          t("resetPassword.password.label") || "New Password"
                        }
                        type="password"
                        placeholder={
                          t("resetPassword.password.placeholder") ||
                          "Enter your new password"
                        }
                        error={
                          form.touched.password &&
                          typeof form.errors.password === "string"
                            ? form.errors.password
                            : ""
                        }
                      />
                    )}
                  </Field>

                  <Field name="confirmPassword">
                    {({ field, form }) => (
                      <FormikInput
                        {...field}
                        name="confirmPassword"
                        label={
                          t("resetPassword.confirmPassword.label") ||
                          "Confirm Password"
                        }
                        type="password"
                        placeholder={
                          t("resetPassword.confirmPassword.placeholder") ||
                          "Confirm your new password"
                        }
                        error={
                          form.touched.confirmPassword &&
                          typeof form.errors.confirmPassword === "string"
                            ? form.errors.confirmPassword
                            : ""
                        }
                      />
                    )}
                  </Field>
                </div>

                <div className="tw-flex tw-justify-center">
                  <HeadlessButton
                    type="submit"
                    disabled={isLoading}
                    className="tw-w-full tw-bg-primary-light tw-text-white tw-py-2 tw-rounded-md tw-font-semibold"
                  >
                    {isLoading ? (
                      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        {t("resetPassword.processing") || "Processing..."}
                        <Spinner />
                      </div>
                    ) : (
                      t("resetPassword.button") || "Reset Password"
                    )}
                  </HeadlessButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
}
